import i18n from '@/i18n';
/** Studio 本地上传：50MB 硬拦 + 温和压缩后再传图库（T-A17 可走 Worker 池） */
import { compressImageForDatabase } from '@/utils/imageUpload';
import { buildUploadFileFromBlob, validateIpeUploadFile } from '@/utils/ipeImageUploadFormats';
import { dataUrlToBlob } from '@/utils/imageUrlUtils';
import { withUploadCompressSlot, mapWithUploadConcurrency, UPLOAD_COMPRESS_CONCURRENCY } from '@/utils/globalUploadBudget';
import { UPLOAD_CONFIG, UPLOAD_MAX_BYTES } from '@/utils/uploadConfig';
import {
  compressUploadFileWithWorker,
  getUploadWorkerPoolSize,
  isWorkerCompressEnabled,
} from '@/utils/imageCompressWorkerPool';
import { readFileAsDataUrl } from './agentImageUtils';
import { loadImageDimensionsFromFile, IMG2IMG_MAX_EDGE_PX } from './img2imgReference';

export const STUDIO_COMPRESSED_UPLOAD_MAX_BYTES = UPLOAD_MAX_BYTES;
export const STUDIO_UPLOAD_MAX_EDGE_PX = IMG2IMG_MAX_EDGE_PX;
export const STUDIO_UPLOAD_DIMENSION_EXCEEDED_MESSAGE =
  '分辨率不可以大于4096x4096无法上传';
/** @deprecated 使用 STUDIO_UPLOAD_MAX_EDGE_PX */
export const INFRINGEMENT_UPLOAD_MAX_EDGE_PX = STUDIO_UPLOAD_MAX_EDGE_PX;
/** @deprecated 使用 STUDIO_UPLOAD_DIMENSION_EXCEEDED_MESSAGE */
export const INFRINGEMENT_UPLOAD_DIMENSION_EXCEEDED_MESSAGE = STUDIO_UPLOAD_DIMENSION_EXCEEDED_MESSAGE;

/**
 * @param {File} file
 * @param {{ maxEdgePx?: number, dimensionExceededMessage?: string }} [options]
 * @returns {Promise<{ ok: true, file: File, contentHash?: string } | { ok: false, message: string }>}
 */
async function prepareStudioCompressedUploadFileMainThread(file, options = {}) {
  const dims = await loadImageDimensionsFromFile(file);
  if (!dims?.width || !dims?.height) {
    return { ok: false, message: i18n.t('workbench:ipeUtilError.cannotReadImageSize') };
  }

  const maxEdgePx = Number(options.maxEdgePx) || 0;
  if (maxEdgePx > 0 && (dims.width > maxEdgePx || dims.height > maxEdgePx)) {
    return {
      ok: false,
      message: options.dimensionExceededMessage || STUDIO_UPLOAD_DIMENSION_EXCEEDED_MESSAGE,
    };
  }

  let uploadFile;
  try {
    uploadFile = await withUploadCompressSlot(async () => {
      const dataUrl = await readFileAsDataUrl(file);
      const compressed = await compressImageForDatabase(dataUrl, dims.width, dims.height);
      const blob = dataUrlToBlob(compressed);
      if (!blob?.size) {
        throw new Error(i18n.t('workbench:ipeUtilError.imageCompressFailed'));
      }
      return buildUploadFileFromBlob(blob, { filename: file.name, dataUrlHint: compressed });
    });
  } catch {
    return { ok: false, message: i18n.t('workbench:ipeUtilError.imageCompressFailedRetry') };
  }

  if (uploadFile.size > STUDIO_COMPRESSED_UPLOAD_MAX_BYTES) {
    return { ok: false, message: i18n.t('workbench:ipeUtilError.imageStillOver10mb') };
  }

  return { ok: true, file: uploadFile };
}

/**
 * @param {File} file
 * @param {{ maxEdgePx?: number, dimensionExceededMessage?: string }} [options]
 * @returns {Promise<{ ok: true, file: File, contentHash?: string } | { ok: false, message: string }>}
 */
async function prepareStudioCompressedUploadFileViaWorker(file, options = {}) {
  const result = await compressUploadFileWithWorker(file, {
    jpegQuality: UPLOAD_CONFIG.jpegQuality,
    compressMaxEdgePx: UPLOAD_CONFIG.maxEdgePx,
    rejectMaxEdgePx: Number(options.maxEdgePx) || 0,
    dimensionExceededMessage: options.dimensionExceededMessage || STUDIO_UPLOAD_DIMENSION_EXCEEDED_MESSAGE,
  });
  if (!result.ok) {
    // Worker 失败 → 单张降级主线程（能力瞬时失败 / 解码异常）
    if (result.error === 'dimension_exceeded') {
      return { ok: false, message: result.message };
    }
    return prepareStudioCompressedUploadFileMainThread(file, options);
  }
  if (result.file.size > STUDIO_COMPRESSED_UPLOAD_MAX_BYTES) {
    return { ok: false, message: i18n.t('workbench:ipeUtilError.imageStillOver10mb') };
  }
  return {
    ok: true,
    file: result.file,
    contentHash: result.contentHash || result.file?._contentHash || '',
  };
}

/**
 * @param {File} file
 * @param {{ maxEdgePx?: number, dimensionExceededMessage?: string }} [options]
 * @returns {Promise<{ ok: true, file: File, contentHash?: string } | { ok: false, message: string }>}
 */
export async function prepareStudioCompressedUploadFile(file, options = {}) {
  const formatError = validateIpeUploadFile(file);
  if (formatError) {
    return { ok: false, message: formatError };
  }
  if (!file.size || file.size > STUDIO_COMPRESSED_UPLOAD_MAX_BYTES) {
    return { ok: false, message: i18n.t('workbench:ipeUtilError.imageOver10mb') };
  }

  const result = isWorkerCompressEnabled()
    ? await prepareStudioCompressedUploadFileViaWorker(file, options)
    : await prepareStudioCompressedUploadFileMainThread(file, options);

  // T-A18：压缩后 File 保留乐观 localId，便于 streaming 复用同一张卡
  if (result?.ok && result.file && file?._optimisticLocalId) {
    try {
      Object.defineProperty(result.file, '_optimisticLocalId', {
        value: String(file._optimisticLocalId),
        enumerable: false,
        configurable: true,
        writable: true,
      });
    } catch {
      result.file._optimisticLocalId = String(file._optimisticLocalId);
    }
  }
  return result;
}

/**
 * @param {File[]} files
 * @param {{ maxEdgePx?: number, dimensionExceededMessage?: string }} [options]
 * @returns {Promise<{ files: File[], errors: string[], contentHashes: string[] }>}
 */
export async function prepareStudioCompressedUploadFiles(files, options = {}) {
  const list = Array.isArray(files) ? files : [];
  if (list.length === 0) {
    return { files: [], errors: [], contentHashes: [] };
  }
  const concurrency = isWorkerCompressEnabled()
    ? getUploadWorkerPoolSize()
    : UPLOAD_COMPRESS_CONCURRENCY;
  const results = await mapWithUploadConcurrency(
    list,
    concurrency,
    (file) => prepareStudioCompressedUploadFile(file, options),
  );
  const prepared = [];
  const contentHashes = [];
  const errors = [];
  for (const result of results) {
    if (result?.ok) {
      prepared.push(result.file);
      contentHashes.push(result.contentHash || result.file?._contentHash || '');
    } else if (result?.message) {
      errors.push(result.message);
    }
  }
  return { files: prepared, errors, contentHashes };
}
